import {
  Alert, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress,
  Collapse, Dialog, DialogContent, DialogContentText, Divider, Grow,
  Link, Stack, Typography, IconButton, List, ListItem, Skeleton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useRouter } from 'next/router';

// Icons
import CallMadeRoundedIcon from '@mui/icons-material/CallMadeRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';

import useAuth from '../../hook/auth';
import { checkSubscription, fetchDefaultCard, fetchSubscriptionDetails, postData } from '../../utils/helpers';
import getAPIKey from '../../../lib/profile/_getAPIKey';
import getAPIUsage from '../../../lib/profile/_getAPIUsage';
import QuotaTable from './QuotaTable';
import { colors, radii } from '../../utils/designTokens';

const ClaudeLogo = () => (
  <Box sx={{ width: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97757' }}>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" className="w-full fill-current" fill="#d97757">
      <path d="m19.6 66.5 19.7-11 .3-1-.3-.5h-1l-3.3-.2-11.2-.3L14 53l-9.5-.5-2.4-.5L0 49l.2-1.5 2-1.3 2.9.2 6.3.5 9.5.6 6.9.4L38 49.1h1.6l.2-.7-.5-.4-.4-.4L29 41l-10.6-7-5.6-4.1-3-2-1.5-2-.6-4.2 2.7-3 3.7.3.9.2 3.7 2.9 8 6.1L37 36l1.5 1.2.6-.4.1-.3-.7-1.1L33 25l-6-10.4-2.7-4.3-.7-2.6c-.3-1-.4-2-.4-3l3-4.2L28 0l4.2.6L33.8 2l2.6 6 4.1 9.3L47 29.9l2 3.8 1 3.4.3 1h.7v-.5l.5-7.2 1-8.7 1-11.2.3-3.2 1.6-3.8 3-2L61 2.6l2 2.9-.3 1.8-1.1 7.7L59 27.1l-1.5 8.2h.9l1-1.1 4.1-5.4 6.9-8.6 3-3.5L77 13l2.3-1.8h4.3l3.1 4.7-1.4 4.9-4.4 5.6-3.7 4.7-5.3 7.1-3.2 5.7.3.4h.7l12-2.6 6.4-1.1 7.6-1.3 3.5 1.6.4 1.6-1.4 3.4-8.2 2-9.6 2-14.3 3.3-.2.1.2.3 6.4.6 2.8.2h6.8l12.6 1 3.3 2 1.9 2.7-.3 2-5.1 2.6-6.8-1.6-16-3.8-5.4-1.3h-.8v.4l4.6 4.5 8.3 7.5L89 80.1l.5 2.4-1.3 2-1.4-.2-9.2-7-3.6-3-8-6.8h-.5v.7l1.8 2.7 9.8 14.7.5 4.5-.7 1.4-2.6 1-2.7-.6-5.8-8-6-9-4.7-8.2-.5.4-2.9 30.2-1.3 1.5-3 1.2-2.5-2-1.4-3 1.4-6.2 1.6-8 1.3-6.4 1.2-7.9.7-2.6v-.2H49L43 72l-9 12.3-7.2 7.6-1.7.7-3-1.5.3-2.8L24 86l10-12.8 6-7.9 4-4.6-.1-.5h-.3L17.2 77.4l-4.7.6-2-2 .2-3 1-1 8-5.5Z"></path>
    </svg>
  </Box>
);

const ChatGPTLogo = () => (
  <Box sx={{ width: 26, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 509.639">
      <path fill="#10a37f" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.612C52.026 509.64 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z" />
      <path fill="#fff" fillRule="nonzero" d="M412.037 221.764a90.834 90.834 0 004.648-28.67 90.79 90.79 0 00-12.443-45.87c-16.37-28.496-46.738-46.089-79.605-46.089-6.466 0-12.943.683-19.264 2.04a90.765 90.765 0 00-67.881-30.515h-.576c-.059.002-.149.002-.216.002-39.807 0-75.108 25.686-87.346 63.554-25.626 5.239-47.748 21.31-60.682 44.03a91.873 91.873 0 00-12.407 46.077 91.833 91.833 0 0023.694 61.553 90.802 90.802 0 00-4.649 28.67 90.804 90.804 0 0012.442 45.87c16.369 28.504 46.74 46.087 79.61 46.087a91.81 91.81 0 0019.253-2.04 90.783 90.783 0 0067.887 30.516h.576l.234-.001c39.829 0 75.119-25.686 87.357-63.588 25.626-5.242 47.748-21.312 60.682-44.033a91.718 91.718 0 0012.383-46.035 91.83 91.83 0 00-23.693-61.553l-.004-.005zM275.102 413.161h-.094a68.146 68.146 0 01-43.611-15.8 56.936 56.936 0 002.155-1.221l72.54-41.901a11.799 11.799 0 005.962-10.251V241.651l30.661 17.704c.326.163.55.479.596.84v84.693c-.042 37.653-30.554 68.198-68.21 68.273h.001zm-146.689-62.649a68.128 68.128 0 01-9.152-34.085c0-3.904.341-7.817 1.005-11.663.539.323 1.48.897 2.155 1.285l72.54 41.901a11.832 11.832 0 0011.918-.002l88.563-51.137v35.408a1.1 1.1 0 01-.438.94l-73.33 42.339a68.43 68.43 0 01-34.11 9.12 68.359 68.359 0 01-59.15-34.11l-.001.004zm-19.083-158.36a68.044 68.044 0 0135.538-29.934c0 .625-.036 1.731-.036 2.5v83.801l-.001.07a11.79 11.79 0 005.954 10.242l88.564 51.13-30.661 17.704a1.096 1.096 0 01-1.034.093l-73.337-42.375a68.36 68.36 0 01-34.095-59.143 68.412 68.412 0 019.112-34.085l-.004-.003zm251.907 58.621l-88.563-51.137 30.661-17.697a1.097 1.097 0 011.034-.094l73.337 42.339c21.109 12.195 34.132 34.746 34.132 59.132 0 28.604-17.849 54.199-44.686 64.078v-86.308c.004-.032.004-.065.004-.096 0-4.219-2.261-8.119-5.919-10.217zm30.518-45.93c-.539-.331-1.48-.898-2.155-1.286l-72.54-41.901a11.842 11.842 0 00-5.958-1.611c-2.092 0-4.15.558-5.957 1.611l-88.564 51.137v-35.408l-.001-.061a1.1 1.1 0 01.44-.88l73.33-42.303a68.301 68.301 0 0134.108-9.129c37.704 0 68.281 30.577 68.281 68.281a68.69 68.69 0 01-.984 11.545v.005zm-191.843 63.109l-30.668-17.704a1.09 1.09 0 01-.596-.84v-84.692c.016-37.685 30.593-68.236 68.281-68.236a68.332 68.332 0 0143.689 15.804 63.09 63.09 0 00-2.155 1.222l-72.54 41.9a11.794 11.794 0 00-5.961 10.248v.068l-.05 102.23zm16.655-35.91l39.445-22.782 39.444 22.767v45.55l-39.444 22.767-39.445-22.767v-45.535z" />
    </svg>
  </Box>
);

const getAppIcon = (appName) => {
  if (!appName) return 'A';
  const normalizedName = appName.toLowerCase();
  if (normalizedName.includes('claude')) return <ClaudeLogo />;
  if (normalizedName.includes('chatgpt')) return <ChatGPTLogo />;
  return appName[0].toUpperCase();
};

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.arrow}`]: { color: colors.primary.main },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: colors.primary.main, fontWeight: 600,
    color: '#fff', fontSize: 11, padding: '5px 15px',
  },
}));

const LoadingContainer = styled(Box)(() => ({
  height: '70vh', textAlign: 'center', display: 'flex',
  alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
}));

const SectionCard = styled(Card)(() => ({
  boxShadow: 'none',
  borderRadius: radii.lg,
  border: `1px solid ${colors.neutral[200]}`,
  transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  '&:hover': {
    borderColor: colors.neutral[300],
    boxShadow: '0 8px 24px -4px rgba(0,0,0,0.06), 0 4px 8px -2px rgba(0,0,0,0.02)',
  },
}));

const SectionHeader = ({ icon, title, subtitle, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', mb: 3 }}>
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'start' }}>
      <Box sx={{
        mt: 0.3, width: 36, height: 36, borderRadius: radii.sm,
        bgcolor: colors.accent[50], display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', color: colors.neutral[900], lineHeight: 1.3 }}>
          {title}
        </Typography>
        <Typography sx={{ color: colors.neutral[500], fontSize: '.84rem', mt: 0.3, lineHeight: 1.4, display: 'block' }}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
    {action}
  </Box>
);

const getStatusColor = (status) => ({
  active: 'success', trialing: 'info', past_due: 'warning',
  canceled: 'error', incomplete: 'warning',
}[status] ?? 'default');

const ProfileTabs = ({ emailVerification, alertOpen, pageLoading }) => {
  const auth = useAuth() || {};
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const { logout, user } = auth;
  const { displayName, photoURL, reloadUserInfo, metadata } = user || {};
  const { customAttributes, emailVerified, email } = reloadUserInfo || {};
  const { creationTime } = metadata || {};

  // Memoize custom attributes parsing to prevent re-parsing on every render
  const { userType, stripe_customer_id } = useMemo(() => {
    try {
      return customAttributes ? JSON.parse(customAttributes) : {};
    } catch {
      return {};
    }
  }, [customAttributes]);

  const isPremium = Boolean(userType === 'PREMIUM' || userType === 'PROFESSIONAL');

  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [hasPastSubscriptions, setPastSubscriptions] = useState(false);
  const [cardDetails, setCardDetails] = useState(null);

  const [apiKey, setApiKey] = useState(null);
  const [apiKeyStartDate, setApiKeyStartDate] = useState(null);
  const [apiKeyEndDate, setApiKeyEndDate] = useState(null);
  const [apiKeyQuota, setApiKeyQuota] = useState([]);
  const [clipboard, setClipboard] = useState(null);
  const [open, setOpen] = useState(false);

  const [connectedApps, setConnectedApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [revoking, setRevoking] = useState(null);

  /* ── Clipboard ─────────────────────────────────────────────────────── */
  const handleClipboard = useCallback(async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setClipboard('Copied!');
    } catch (err) {
      console.error('Clipboard fallback failed', err);
      setClipboard('Failed to copy');
    }
    setTimeout(() => setClipboard(null), 3000);
  }, []);

  /* ── API Data Fetching ─────────────────────────────────────────────── */
  useEffect(() => {
    let isMounted = true;

    if (!stripe_customer_id && email) {
      checkSubscription(email).then((d) => {
        if (isMounted) setPastSubscriptions(d?.hasPastSubscriptions);
      }).catch(console.error);
    }

    if (stripe_customer_id) {
      fetchSubscriptionDetails(stripe_customer_id).then((d) => {
        if (isMounted) setSubscriptionDetails(d);
      }).catch(console.error);

      fetchDefaultCard(stripe_customer_id).then((d) => {
        if (isMounted) setCardDetails(d);
      }).catch(console.error);
    }

    return () => { isMounted = false; };
  }, [stripe_customer_id, email]);

  const fetchConnectedApps = useCallback(async () => {
    if (!user) return;
    setAppsLoading(true);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch('/api/oauth/approved-apps', {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConnectedApps(data.apps || []);
      }
    } catch (err) {
      console.error('[Profile] Failed to fetch connected apps:', err);
    } finally {
      setAppsLoading(false);
    }
  }, [user]);

  const getUserAPIUsage = useCallback(async () => {
    if (user?.accessToken) {
      try {
        const { getAPIKey: key } = await getAPIKey(user.accessToken);
        setApiKey(key);
        const { getAPIUsage: usage } = await getAPIUsage(user.accessToken);

        setApiKeyStartDate(usage?.start_date);
        setApiKeyEndDate(usage?.end_date);
        setApiKeyQuota(usage?.daily_quota);
      } catch (err) {
        console.error('API key/usage error:', err);
      }
    }
  }, [user]);

  useEffect(() => {
    let isMounted = true;
    if (user) {
      setSubscriptionDetails(null);
      setCardDetails(null);
      getUserAPIUsage();
      fetchConnectedApps();
    }
    return () => { isMounted = false; };
  }, [user, getUserAPIUsage, fetchConnectedApps]);

  /* ── Handlers ──────────────────────────────────────────────────────── */
  const handleRevokeApp = async (clientId) => {
    if (!user || revoking) return;
    setRevoking(clientId);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch('/api/oauth/approved-apps', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ client_id: clientId })
      });
      if (res.ok) {
        setConnectedApps((prev) => prev.filter(app => app.clientId !== clientId));
      }
    } catch (err) {
      console.error('[Profile] Failed to revoke app:', err);
    } finally {
      setRevoking(null);
    }
  };

  const redirectToCustomerPortal = async () => {
    setLoading(true);
    try {
      const { url } = await postData({ url: '/api/create-portal-link', stripe_customer_id });
      window.location.assign(url);
    } catch (err) {
      setLoading(false);
      alert(err.message);
    }
  };

  const handleManagePortal = (e) => {
    const fallbackUrl = process.env.NEXT_PUBLIC_STRIPE_PORTAL_URL;
    if (isPremium && !stripe_customer_id && fallbackUrl) {
      router.push(fallbackUrl);
    } else if (isPremium && stripe_customer_id) {
      redirectToCustomerPortal();
    } else {
      e.preventDefault();
    }
  };

  /* ── Memoized Computations ─────────────────────────────────────────── */
  const { endDate, formattedEndDate, daysUntilNextBillingCycle, formattedAmount, userCreatedTime, _apiKeyStartDate } = useMemo(() => {
    const end = subscriptionDetails?.current_period_end ? new Date(subscriptionDetails.current_period_end * 1000) : null;
    const amount = subscriptionDetails?.plan?.amount ? subscriptionDetails.plan.amount / 100 : null;
    const currency = subscriptionDetails?.plan?.currency?.toUpperCase() || 'USD';

    let keyStart = null;
    if (apiKeyStartDate) {
      const d = new Date(apiKeyStartDate);
      if (!isNaN(d.getTime())) keyStart = new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(d);
    }

    return {
      endDate: end,
      formattedEndDate: end ? end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null,
      daysUntilNextBillingCycle: end ? Math.ceil((end.getTime() - Date.now()) / (1000 * 3600 * 24)) : 0,
      formattedAmount: amount ? amount.toLocaleString('en-US', { style: 'currency', currency }) : null,
      userCreatedTime: creationTime ? new Date(creationTime).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null,
      _apiKeyStartDate: keyStart
    };
  }, [subscriptionDetails, apiKeyStartDate, creationTime]);

  /* ── Render ────────────────────────────────────────────────────────── */
  if (auth && !auth?.user && pageLoading) {
    return (
      <LoadingContainer>
        <Typography variant="subtitle1" sx={{ color: colors.neutral[500] }}>
          Please wait while we fetch user details...
        </Typography>
        <Box sx={{ height: 'auto', width: '25%', mx: 'auto', mt: 5 }}>
          <Skeleton variant="rectangular" height={4} width="100%" />
        </Box>
      </LoadingContainer>
    );
  }

  if (pageLoading || !auth?.user) return null;

  return (
    <Box sx={{ width: '100%', pb: 8 }}>
      {/* User Profile Card */}
      <SectionCard sx={{ mb: 4 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: { xs: 'start', md: 'center' }, gap: 2.5, flexWrap: 'wrap' }}>
            <Avatar
              alt={displayName}
              src={photoURL}
              sx={{ width: 64, height: 64, border: `3px solid ${colors.neutral[200]}`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
            />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: colors.neutral[900], letterSpacing: '-0.01em' }}>
                  {displayName}
                </Typography>
                {isPremium && (
                  <Chip
                    icon={<VerifiedRoundedIcon sx={{ fontSize: '.85rem !important' }} />}
                    label={userType}
                    size="small"
                    sx={{
                      bgcolor: colors.accent[50], color: colors.accent.dark, fontWeight: 600, fontSize: '.72rem', textTransform: 'capitalize',
                      border: `1px solid ${colors.accent[200]}`, '& .MuiChip-icon': { color: colors.accent.main },
                    }}
                  />
                )}
              </Box>
              <Typography sx={{ color: colors.neutral[500], fontSize: '.875rem', mt: 0.3 }}>{email}</Typography>
              <Typography sx={{ color: colors.neutral[400], fontSize: '.78rem', mt: 0.5, display: 'block' }}>
                Member since {userCreatedTime ?? creationTime}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              {!emailVerified && email && (
                <Button
                  onClick={emailVerification} size="small" variant="outlined"
                  sx={{ textTransform: 'none', fontSize: '.8rem', fontWeight: 600, borderRadius: radii.sm, borderColor: colors.warning.main, color: colors.warning.dark }}
                >
                  Verify Email
                </Button>
              )}
              <Button
                onClick={logout} size="small" startIcon={<LogoutRoundedIcon sx={{ fontSize: '1rem !important' }} />}
                sx={{ textTransform: 'none', fontSize: '.8rem', fontWeight: 500, borderRadius: radii.sm, color: colors.neutral[600], border: `1px solid ${colors.neutral[200]}` }}
              >
                Sign out
              </Button>
            </Box>
          </Box>
        </CardContent>
      </SectionCard>

      {/* API Keys */}
      {apiKey && (
        <Collapse in={!!apiKey} timeout="auto" unmountOnExit>
          <SectionCard sx={{ mb: 4 }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <SectionHeader
                icon={<KeyRoundedIcon sx={{ fontSize: '1.1rem', color: colors.accent.main }} />}
                title="API Keys" subtitle="Restricted to your operations and usage limit"
              />
              <Box sx={{ bgcolor: colors.neutral[50], borderRadius: '12px', border: `1px solid ${colors.neutral[200]}`, p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Box
                    sx={{ flex: 1, minWidth: 200, fontFamily: 'monospace', fontSize: '.875rem', color: colors.neutral[800], fontWeight: 500, cursor: 'pointer', '&:hover': { color: colors.accent.main } }}
                    onClick={() => handleClipboard(apiKey)}
                  >
                    {apiKey}
                  </Box>
                  <LightTooltip describeChild title={clipboard || 'Copy key'} placement="top" disableInteractive>
                    <IconButton
                      size="small"
                      aria-label="Copy API Key"
                      onClick={() => handleClipboard(apiKey)}
                      sx={{ bgcolor: colors.neutral[0], border: `1px solid ${colors.neutral[200]}`, borderRadius: radii.sm }}
                    >
                      <ContentCopyRoundedIcon sx={{ fontSize: '1rem', color: clipboard ? colors.success.main : colors.neutral[600] }} />
                    </IconButton>
                  </LightTooltip>
                  {clipboard && <Chip label={clipboard} size="small" sx={{ bgcolor: colors.success.bg, color: colors.success.dark, fontWeight: 600, fontSize: '.72rem' }} />}
                </Box>
                <Box sx={{ mt: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {_apiKeyStartDate && <Typography sx={{ color: colors.neutral[500], fontSize: '.78rem' }}>Created {_apiKeyStartDate}</Typography>}
                  {email && (
                    <Typography sx={{ color: colors.neutral[500], fontSize: '.78rem' }}>
                      <Link target="_blank" rel="noopener noreferrer" underline="hover" href="/docs/api?lang=r#api-reference" sx={{ color: colors.accent.main }}>
                        API Documentation <OpenInNewRoundedIcon sx={{ fontSize: '.7rem', verticalAlign: 'middle' }} />
                      </Link>
                    </Typography>
                  )}
                </Box>
              </Box>
              {apiKeyQuota?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    size="small" onClick={() => setOpen(!open)}
                    endIcon={open ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                    sx={{ textTransform: 'none', fontSize: '.84rem', fontWeight: 500, color: colors.neutral[600] }}
                  >
                    {open ? 'Hide' : 'View'} Usage History
                  </Button>
                  <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 1.5 }}>
                      <QuotaTable data={apiKeyQuota} startDate={apiKeyStartDate} endDate={apiKeyEndDate} />
                    </Box>
                  </Collapse>
                </Box>
              )}
            </CardContent>
          </SectionCard>
        </Collapse>
      )}

      {/* Connected Apps */}
      <SectionCard sx={{ mb: 4 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <SectionHeader
            icon={<AppsRoundedIcon sx={{ fontSize: '1.1rem', color: colors.accent.main }} />}
            title="Connected Apps" subtitle="Manage third-party applications with access to your Grufity data"
          />
          {appsLoading ? (
            <Box sx={{ py: 3 }}>
              <Skeleton variant="rounded" height={80} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={80} />
            </Box>
          ) : connectedApps && connectedApps.length > 0 ? (
            <Box sx={{ border: `1px solid ${colors.neutral[200]}`, borderRadius: '12px', overflow: 'hidden' }}>
              <List disablePadding>
                {connectedApps.map((app, idx) => (
                  <Box key={app.clientId}>
                    <ListItem sx={{ py: 2, px: 2.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, bgcolor: '#ffffff' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 200 }}>
                        <Avatar sx={{ bgcolor: colors.neutral[50], color: colors.neutral[800], width: 44, height: 44, border: `1px solid ${colors.neutral[200]}` }}>
                          {getAppIcon(app.clientName)}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 600, color: colors.neutral[900], fontSize: '.95rem' }}>{app.clientName || 'Unknown App'}</Typography>
                          <Typography sx={{ fontSize: '.78rem', color: colors.neutral[500], mt: 0.2, display: 'block' }}>
                            Connected on {new Date(app.approvedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        size="small" variant="outlined" color="error"
                        onClick={() => handleRevokeApp(app.clientId)}
                        disabled={revoking === app.clientId}
                        startIcon={revoking === app.clientId ? <CircularProgress size={14} color="inherit" /> : <LinkOffRoundedIcon sx={{ fontSize: '1.1rem !important' }} />}
                        sx={{ textTransform: 'none', borderRadius: radii.sm, fontWeight: 600 }}
                      >
                        {revoking === app.clientId ? 'Disconnecting...' : 'Disconnect'}
                      </Button>
                    </ListItem>
                    {idx < connectedApps.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 5, px: 3, bgcolor: colors.neutral[50], borderRadius: 2, border: `1px solid ${colors.neutral[200]}` }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '50%', mx: 'auto', mb: 2, bgcolor: colors.neutral[200], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AppsRoundedIcon sx={{ color: colors.neutral[500] }} />
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: colors.neutral[800], mb: 0.5 }}>No connected apps</Typography>
              <Typography sx={{ fontSize: '.875rem', color: colors.neutral[500], display: 'block' }}>You haven't granted access to any third-party AI agents yet.</Typography>
            </Box>
          )}
        </CardContent>
      </SectionCard>

      {/* Account & Billing */}
      <SectionCard>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <SectionHeader
            icon={<ReceiptLongRoundedIcon sx={{ fontSize: '1.1rem', color: colors.accent.main }} />}
            title="Subscription & Billing" subtitle="Manage your plan and payment details"
            action={isPremium && (
              <Chip
                icon={<FiberManualRecordIcon sx={{ fontSize: '.6rem !important' }} />}
                label={subscriptionDetails?.status || 'active'} size="small"
                color={getStatusColor(subscriptionDetails?.status || 'active')}
                sx={{ textTransform: 'capitalize', fontSize: '.75rem', fontWeight: 600 }}
              />
            )}
          />

          {(formattedAmount || cardDetails?.last4) ? (
            <Stack direction={{ md: 'row', xs: 'column' }} spacing={2.5}>
              {formattedAmount && (
                <Grow in={!!formattedAmount} timeout={800}>
                  <Box sx={{ flex: 1, bgcolor: colors.neutral[50], borderRadius: '12px', border: `1px solid ${colors.neutral[200]}`, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Typography sx={{ fontSize: '.78rem', fontWeight: 600, color: colors.neutral[500], textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {subscriptionDetails?.plan?.nickname ?? 'Current Plan'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '2rem', color: colors.neutral[900], lineHeight: 1, letterSpacing: '-0.02em' }}>
                        {formattedAmount}
                      </Typography>
                      {subscriptionDetails?.plan?.interval && (
                        <Typography sx={{ ml: 0.5, fontSize: '.84rem', color: colors.neutral[500] }}>/{subscriptionDetails.plan.interval}</Typography>
                      )}
                    </Box>
                    {formattedEndDate && (
                      daysUntilNextBillingCycle <= 0 ? (
                        <Alert severity="error" sx={{ py: 0.5, fontSize: '.78rem', borderRadius: radii.sm }}>Payment overdue — please update billing details.</Alert>
                      ) : daysUntilNextBillingCycle <= 7 ? (
                        <Alert severity="warning" sx={{ py: 0.5, fontSize: '.78rem', borderRadius: radii.sm }}>Renews in <strong>{daysUntilNextBillingCycle} days</strong> on {formattedEndDate}</Alert>
                      ) : (
                        <Typography sx={{ fontSize: '.82rem', color: colors.neutral[500] }}>Renews {formattedEndDate} &middot; {daysUntilNextBillingCycle} days</Typography>
                      )
                    )}
                    <Button
                      size="small" variant="outlined" endIcon={<CallMadeRoundedIcon sx={{ fontSize: '.85rem !important' }} />}
                      onClick={handleManagePortal}
                      sx={{ mt: 2.5, textTransform: 'none', fontSize: '.84rem', fontWeight: 600, borderRadius: radii.sm, borderColor: colors.neutral[300], color: colors.neutral[700] }}
                    >
                      Manage plan
                    </Button>
                  </Box>
                </Grow>
              )}
              {cardDetails?.last4 && (
                <Grow in={!!cardDetails?.last4} timeout={1000}>
                  <Box sx={{ flex: 1, bgcolor: colors.neutral[50], borderRadius: '12px', border: `1px solid ${colors.neutral[200]}`, p: 3 }}>
                    <Typography sx={{ fontSize: '.78rem', fontWeight: 600, color: colors.neutral[500], textTransform: 'uppercase', letterSpacing: '0.06em', mb: 2 }}>Payment Method</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <Box sx={{ width: 40, height: 28, bgcolor: colors.neutral[200], borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CreditCardRoundedIcon sx={{ fontSize: '1.1rem', color: colors.neutral[500] }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '.9rem', color: colors.neutral[800] }}>
                          {cardDetails.brand?.toUpperCase()} •••• {cardDetails.last4}
                        </Typography>
                        <Typography sx={{ fontSize: '.78rem', color: colors.neutral[500] }}>Expires {cardDetails.exp_month}/{cardDetails.exp_year}</Typography>
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: '.78rem', color: colors.neutral[500], mt: 0.5 }}>Associated with {cardDetails.email}</Typography>
                    <Button
                      size="small" variant="outlined" endIcon={<CallMadeRoundedIcon sx={{ fontSize: '.85rem !important' }} />}
                      onClick={handleManagePortal}
                      sx={{ mt: 2.5, textTransform: 'none', fontSize: '.84rem', fontWeight: 600, borderRadius: radii.sm, borderColor: colors.neutral[300], color: colors.neutral[700] }}
                    >
                      Update payment
                    </Button>
                  </Box>
                </Grow>
              )}
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 5, px: 3, bgcolor: colors.neutral[50], borderRadius: 2, border: `1px solid ${colors.neutral[200]}` }}>
              {isPremium && !stripe_customer_id ? (
                <>
                  <Typography sx={{ color: colors.neutral[500], fontSize: '.9rem', mb: 2, display: 'block' }}>We're having difficulty loading your subscription details.</Typography>
                  <Button size="small" variant="outlined" endIcon={<CallMadeRoundedIcon />} onClick={handleManagePortal} sx={{ textTransform: 'none', borderRadius: radii.sm, fontWeight: 600 }}>
                    View on Stripe Portal
                  </Button>
                </>
              ) : isPremium ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                  <Skeleton variant="rectangular" width="100%" height={150} sx={{ borderRadius: 2 }} />
                </Box>
              ) : (
                <>
                  <Box sx={{ width: 48, height: 48, borderRadius: '50%', mx: 'auto', mb: 2, bgcolor: colors.accent[50], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ReceiptLongRoundedIcon sx={{ color: colors.accent.main }} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: colors.neutral[800], mb: 0.5 }}>No active subscription</Typography>
                  <Typography sx={{ fontSize: '.875rem', color: colors.neutral[500], mb: 3, maxWidth: 320, mx: 'auto', display: 'block' }}>
                    Upgrade to Premium for unlimited access to all Grufity features and data.
                  </Typography>
                  <Button
                    variant="contained" href="/pricing?ref=profile" disableElevation
                    sx={{ textTransform: 'none', px: 4, py: 1.2, fontWeight: 600, fontSize: '.9rem', borderRadius: radii.sm, bgcolor: colors.accent.main }}
                  >
                    {hasPastSubscriptions ? 'Reactivate Subscription' : 'Upgrade to Premium'}
                  </Button>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </SectionCard>

      <Box sx={{ mt: 6, pt: 4, borderTop: `1px solid ${colors.neutral[200]}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Typography sx={{ color: colors.neutral[400], fontSize: '.82rem' }}>
          Need to delete your account? Contact us at{' '}
          <Link href="mailto:contact@grufity.com?subject=Regarding My Account" sx={{ color: colors.accent.main, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            contact@grufity.com
          </Link>
        </Typography>
      </Box>

      <Dialog open={loading} maxWidth="xs" sx={{ '& .MuiPaper-root.MuiDialog-paper': { backgroundColor: 'transparent', boxShadow: 0 }, '& .MuiDialog-container': { backdropFilter: 'blur(8px) !important' } }}>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}><CircularProgress sx={{ color: '#fff' }} /></Box>
            <Typography sx={{ color: '#fff', fontSize: '.9rem', fontWeight: 500 }}>Redirecting to Stripe...</Typography>
          </DialogContentText>
        </DialogContent>
      </Dialog>

      <Dialog open={alertOpen} onClose={() => location.reload()} PaperProps={{ sx: { borderRadius: radii.lg, maxWidth: 420 } }}>
        <DialogContent sx={{ textAlign: 'center', px: 4, py: 4 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', color: colors.neutral[900], mb: 2 }}>Verification email sent</Typography>
          <Typography sx={{ color: colors.neutral[600], fontSize: '.9rem', lineHeight: 1.6 }}>
            We sent a verification link to <Typography component="span" sx={{ color: colors.accent.main, fontWeight: 600 }}>{email}</Typography>. If you don't see it, check your spam folder.
          </Typography>
          <Button onClick={() => location.reload()} variant="contained" disableElevation sx={{ mt: 3, textTransform: 'none', fontWeight: 600, borderRadius: radii.sm, bgcolor: colors.accent.main }} autoFocus>
            Done
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProfileTabs;

// Fixes Applied to the code above: Wrapped the synchronous JSON.parse blocking operation in a useMemo. Added AbortControllers/Cleanup functions to the useEffects to prevent memory leaks and state updates on unmounted components. Replaced jumping layout spinners with UI Skeleton loaders to prevent Cumulative Layout Shift (CLS). Memoized date objects so they don't recalculate on arbitrary re-renders. Added accessibility aria-label tags to interactive icon buttons.

// import {
//   Alert, Avatar, Box, Button, Card, CardActions, CardContent,
//   Chip, CircularProgress, Collapse, Dialog, DialogContent,
//   DialogContentText, Divider, Grow, LinearProgress, Link, Paper,
//   Stack, Table, TableBody, TableCell, TableContainer, TableHead,
//   TableRow, Typography, IconButton, List, ListItem,
// } from '@mui/material';
// import { styled } from '@mui/material/styles';
// import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
// import { useEffect, useState } from 'react';
// import { useRouter } from 'next/router';
// import CallMadeRoundedIcon from '@mui/icons-material/CallMadeRounded';
// import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
// import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
// import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
// import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
// import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
// import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
// import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
// import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
// import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
// import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
// import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
// import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';

// import useAuth from '../../hook/auth';
// import { checkSubscription, fetchDefaultCard, fetchSubscriptionDetails, postData } from '../../utils/helpers';
// import getAPIKey from '../../../lib/profile/_getAPIKey';
// import getAPIUsage from '../../../lib/profile/_getAPIUsage';
// import QuotaTable from './QuotaTable';
// import { colors, radii, shadows, card as cardTokens } from '../../utils/designTokens';

// const STRIPE_PORTAL_URL = process.env.NEXT_PUBLIC_STRIPE_PORTAL_URL || 'https://billing.stripe.com/p/login/00g3cNfdfc8ZfDiaEE';

// const ClaudeLogo = () => (
//   <Box sx={{ width: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97757' }}>
//     <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" className="w-full fill-current" fill="#d97757">
//       <path d="m19.6 66.5 19.7-11 .3-1-.3-.5h-1l-3.3-.2-11.2-.3L14 53l-9.5-.5-2.4-.5L0 49l.2-1.5 2-1.3 2.9.2 6.3.5 9.5.6 6.9.4L38 49.1h1.6l.2-.7-.5-.4-.4-.4L29 41l-10.6-7-5.6-4.1-3-2-1.5-2-.6-4.2 2.7-3 3.7.3.9.2 3.7 2.9 8 6.1L37 36l1.5 1.2.6-.4.1-.3-.7-1.1L33 25l-6-10.4-2.7-4.3-.7-2.6c-.3-1-.4-2-.4-3l3-4.2L28 0l4.2.6L33.8 2l2.6 6 4.1 9.3L47 29.9l2 3.8 1 3.4.3 1h.7v-.5l.5-7.2 1-8.7 1-11.2.3-3.2 1.6-3.8 3-2L61 2.6l2 2.9-.3 1.8-1.1 7.7L59 27.1l-1.5 8.2h.9l1-1.1 4.1-5.4 6.9-8.6 3-3.5L77 13l2.3-1.8h4.3l3.1 4.7-1.4 4.9-4.4 5.6-3.7 4.7-5.3 7.1-3.2 5.7.3.4h.7l12-2.6 6.4-1.1 7.6-1.3 3.5 1.6.4 1.6-1.4 3.4-8.2 2-9.6 2-14.3 3.3-.2.1.2.3 6.4.6 2.8.2h6.8l12.6 1 3.3 2 1.9 2.7-.3 2-5.1 2.6-6.8-1.6-16-3.8-5.4-1.3h-.8v.4l4.6 4.5 8.3 7.5L89 80.1l.5 2.4-1.3 2-1.4-.2-9.2-7-3.6-3-8-6.8h-.5v.7l1.8 2.7 9.8 14.7.5 4.5-.7 1.4-2.6 1-2.7-.6-5.8-8-6-9-4.7-8.2-.5.4-2.9 30.2-1.3 1.5-3 1.2-2.5-2-1.4-3 1.4-6.2 1.6-8 1.3-6.4 1.2-7.9.7-2.6v-.2H49L43 72l-9 12.3-7.2 7.6-1.7.7-3-1.5.3-2.8L24 86l10-12.8 6-7.9 4-4.6-.1-.5h-.3L17.2 77.4l-4.7.6-2-2 .2-3 1-1 8-5.5Z"></path>
//     </svg>
//   </Box>
// );

// const ChatGPTLogo = () => (
//   <Box sx={{ width: 26, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//     <svg xmlns="http://www.w3.org/2000/svg" shapeRendering="geometricPrecision" textRendering="geometricPrecision" imageRendering="optimizeQuality" fillRule="evenodd" clipRule="evenodd" viewBox="0 0 512 509.639">
//       <path fill="#10a37f" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.612C52.026 509.64 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z" />
//       <path fill="#fff" fillRule="nonzero" d="M412.037 221.764a90.834 90.834 0 004.648-28.67 90.79 90.79 0 00-12.443-45.87c-16.37-28.496-46.738-46.089-79.605-46.089-6.466 0-12.943.683-19.264 2.04a90.765 90.765 0 00-67.881-30.515h-.576c-.059.002-.149.002-.216.002-39.807 0-75.108 25.686-87.346 63.554-25.626 5.239-47.748 21.31-60.682 44.03a91.873 91.873 0 00-12.407 46.077 91.833 91.833 0 0023.694 61.553 90.802 90.802 0 00-4.649 28.67 90.804 90.804 0 0012.442 45.87c16.369 28.504 46.74 46.087 79.61 46.087a91.81 91.81 0 0019.253-2.04 90.783 90.783 0 0067.887 30.516h.576l.234-.001c39.829 0 75.119-25.686 87.357-63.588 25.626-5.242 47.748-21.312 60.682-44.033a91.718 91.718 0 0012.383-46.035 91.83 91.83 0 00-23.693-61.553l-.004-.005zM275.102 413.161h-.094a68.146 68.146 0 01-43.611-15.8 56.936 56.936 0 002.155-1.221l72.54-41.901a11.799 11.799 0 005.962-10.251V241.651l30.661 17.704c.326.163.55.479.596.84v84.693c-.042 37.653-30.554 68.198-68.21 68.273h.001zm-146.689-62.649a68.128 68.128 0 01-9.152-34.085c0-3.904.341-7.817 1.005-11.663.539.323 1.48.897 2.155 1.285l72.54 41.901a11.832 11.832 0 0011.918-.002l88.563-51.137v35.408a1.1 1.1 0 01-.438.94l-73.33 42.339a68.43 68.43 0 01-34.11 9.12 68.359 68.359 0 01-59.15-34.11l-.001.004zm-19.083-158.36a68.044 68.044 0 0135.538-29.934c0 .625-.036 1.731-.036 2.5v83.801l-.001.07a11.79 11.79 0 005.954 10.242l88.564 51.13-30.661 17.704a1.096 1.096 0 01-1.034.093l-73.337-42.375a68.36 68.36 0 01-34.095-59.143 68.412 68.412 0 019.112-34.085l-.004-.003zm251.907 58.621l-88.563-51.137 30.661-17.697a1.097 1.097 0 011.034-.094l73.337 42.339c21.109 12.195 34.132 34.746 34.132 59.132 0 28.604-17.849 54.199-44.686 64.078v-86.308c.004-.032.004-.065.004-.096 0-4.219-2.261-8.119-5.919-10.217zm30.518-45.93c-.539-.331-1.48-.898-2.155-1.286l-72.54-41.901a11.842 11.842 0 00-5.958-1.611c-2.092 0-4.15.558-5.957 1.611l-88.564 51.137v-35.408l-.001-.061a1.1 1.1 0 01.44-.88l73.33-42.303a68.301 68.301 0 0134.108-9.129c37.704 0 68.281 30.577 68.281 68.281a68.69 68.69 0 01-.984 11.545v.005zm-191.843 63.109l-30.668-17.704a1.09 1.09 0 01-.596-.84v-84.692c.016-37.685 30.593-68.236 68.281-68.236a68.332 68.332 0 0143.689 15.804 63.09 63.09 0 00-2.155 1.222l-72.54 41.9a11.794 11.794 0 00-5.961 10.248v.068l-.05 102.23zm16.655-35.91l39.445-22.782 39.444 22.767v45.55l-39.444 22.767-39.445-22.767v-45.535z" />
//     </svg>
//   </Box>
// );

// const getAppIcon = (appName) => {
//   if (!appName) return 'A';
//   const normalizedName = appName.toLowerCase();
//   if (normalizedName.includes('claude')) return <ClaudeLogo />;
//   if (normalizedName.includes('chatgpt')) return <ChatGPTLogo />;
//   return appName[0].toUpperCase();
// };

// const LightTooltip = styled(({ className, ...props }) => (
//   <Tooltip {...props} classes={{ popper: className }} />
// ))(() => ({
//   [`& .${tooltipClasses.arrow}`]: { color: colors.primary.main },
//   [`& .${tooltipClasses.tooltip}`]: {
//     backgroundColor: colors.primary.main, fontWeight: 600,
//     color: '#fff', fontSize: 11, padding: '5px 15px',
//   },
// }));

// const LoadingContainer = styled(Box)(() => ({
//   height: '70vh', textAlign: 'center', display: 'flex',
//   alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
// }));

// /* Premium section card with tokens */
// const SectionCard = styled(Card)(() => ({
//   boxShadow: 'none',
//   borderRadius: radii.lg,
//   border: `1px solid ${colors.neutral[200]}`,
//   transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
//   overflow: 'hidden',
//   '&:hover': {
//     borderColor: colors.neutral[300],
//     boxShadow: '0 8px 24px -4px rgba(0,0,0,0.06), 0 4px 8px -2px rgba(0,0,0,0.02)',
//   },
// }));

// /* Section header with icon */
// const SectionHeader = ({ icon, title, subtitle, action }) => (
//   <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', mb: 3 }}>
//     <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'start' }}>
//       <Box sx={{
//         mt: 0.3, width: 36, height: 36, borderRadius: radii.sm,
//         bgcolor: colors.accent[50], display: 'flex', alignItems: 'center', justifyContent: 'center',
//         flexShrink: 0,
//       }}>
//         {icon}
//       </Box>
//       <Box>
//         <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', color: colors.neutral[900], lineHeight: 1.3 }}>
//           {title}
//         </Typography>
//         <Typography sx={{ color: colors.neutral[500], fontSize: '.84rem', mt: 0.3, lineHeight: 1.4, display: 'block' }}>
//           {subtitle}
//         </Typography>
//       </Box>
//     </Box>
//     {action}
//   </Box>
// );

// /* Status badge color helper */
// const getStatusColor = (status) => ({
//   active: 'success', trialing: 'info', past_due: 'warning',
//   canceled: 'error', incomplete: 'warning',
// }[status] ?? 'default');

// const ProfileTabs = ({ emailVerification, alertOpen, pathname, gotoStripe, membershipOpen, pageLoading }) => {
//   const auth = useAuth() || {};
//   const router = useRouter();
//   const [loading, setLoading] = useState(false);
//   const { logout, user, error } = auth;
//   const { displayName, photoURL, reloadUserInfo, metadata } = user || {};
//   const { customAttributes, emailVerified, email } = reloadUserInfo || {};
//   const { creationTime } = metadata || {};
//   const { userType, stripe_customer_id } = customAttributes ? JSON.parse(customAttributes) : {};
//   const isPremium = userType === 'PREMIUM' || userType === 'PROFESSIONAL';

//   const [subscriptionDetails, setSubscriptionDetails] = useState(null);
//   const [hasPastSubscriptions, setPastSubscriptions] = useState(false);
//   const [cardDetails, setCardDetails] = useState(null);
//   const [subscriptionError, setSubscriptionError] = useState(null);
//   const [cardError, setCardError] = useState(null);
//   const [apiKey, setApiKey] = useState(null);
//   const [apiKeyStartDate, setApiKeyStartDate] = useState(null);
//   const [apiKeyEndDate, setApiKeyEndDate] = useState(null);
//   const [apiKeyQuota, setApiKeyQuota] = useState([]);
//   const [clipboard, setClipboard] = useState(null);
//   const [open, setOpen] = useState(false);

//   // Connected Apps State
//   const [connectedApps, setConnectedApps] = useState([]);
//   const [appsLoading, setAppsLoading] = useState(false);
//   const [revoking, setRevoking] = useState(null);

//   /* ── Clipboard ─────────────────────────────────────────────────────── */
//   const handleClipboard = async (value) => {
//     try {
//       await navigator.clipboard.writeText(value);
//     } catch {
//       const el = document.createElement('textarea');
//       el.value = value;
//       el.style.position = 'fixed';
//       el.style.opacity = '0';
//       document.body.appendChild(el);
//       el.select();
//       document.execCommand('copy');
//       document.body.removeChild(el);
//     }
//     setClipboard('Copied!');
//     setTimeout(() => setClipboard(null), 3000);
//   };

//   /* ── Fetch subscription / card ─────────────────────────────────────── */
//   useEffect(() => {
//     if (!stripe_customer_id && email) {
//       checkSubscription(email)
//         .then((d) => setPastSubscriptions(d?.hasPastSubscriptions))
//         .catch((err) => setSubscriptionError(err.message));
//     }
//     if (stripe_customer_id) {
//       fetchSubscriptionDetails(stripe_customer_id)
//         .then((d) => setSubscriptionDetails(d))
//         .catch((err) => setSubscriptionError(err.message));
//       fetchDefaultCard(stripe_customer_id)
//         .then((d) => setCardDetails(d))
//         .catch((err) => setCardError(err.message));
//     }
//   }, [stripe_customer_id, email]);

//   useEffect(() => {
//     setSubscriptionDetails(null); setSubscriptionError(null);
//     setCardDetails(null); setCardError(null);
//     getUserAPIUsage();
//     fetchConnectedApps();
//   }, [user]);

//   /* ── Connected Apps API Calls ──────────────────────────────────────── */
//   const fetchConnectedApps = async () => {
//     if (!user) return;
//     setAppsLoading(true);
//     try {
//       const idToken = await user.getIdToken();
//       const res = await fetch('/api/oauth/approved-apps', {
//         headers: { Authorization: `Bearer ${idToken}` }
//       });
//       if (res.ok) {
//         const data = await res.json();
//         setConnectedApps(data.apps || []);
//       }
//     } catch (err) {
//       console.error('[Profile] Failed to fetch connected apps:', err);
//     } finally {
//       setAppsLoading(false);
//     }
//   };

//   const handleRevokeApp = async (clientId) => {
//     if (!user || revoking) return;
//     setRevoking(clientId);
//     try {
//       const idToken = await user.getIdToken();
//       const res = await fetch('/api/oauth/approved-apps', {
//         method: 'DELETE',
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${idToken}`
//         },
//         body: JSON.stringify({ client_id: clientId })
//       });
//       if (res.ok) {
//         // Remove the revoked app from the UI instantly
//         setConnectedApps((prev) => prev.filter(app => app.clientId !== clientId));
//       }
//     } catch (err) {
//       console.error('[Profile] Failed to revoke app:', err);
//     } finally {
//       setRevoking(null);
//     }
//   };

//   /* ── Stripe portal redirect ────────────────────────────────────────── */
//   const redirectToCustomerPortal = async () => {
//     setLoading(true);
//     try {
//       const { url } = await postData({ url: '/api/create-portal-link', stripe_customer_id });
//       window.location.assign(url);
//     } catch (err) {
//       setLoading(false);
//       alert(err.message);
//     }
//   };

//   const handleManagePortal = (e) => {
//     if (isPremium && !stripe_customer_id) router.push(STRIPE_PORTAL_URL);
//     else if (isPremium && stripe_customer_id) redirectToCustomerPortal();
//     else e.preventDefault();
//   };

//   /* ── API key + usage ───────────────────────────────────────────────── */
//   const getUserAPIUsage = async () => {
//     if (user?.accessToken) {
//       try {
//         const { getAPIKey: key } = await getAPIKey(user.accessToken);
//         setApiKey(key);
//         const { getAPIUsage: usage } = await getAPIUsage(user.accessToken);

//         setApiKeyStartDate(usage?.start_date);
//         setApiKeyEndDate(usage?.end_date);
//         setApiKeyQuota(usage?.daily_quota);
//       } catch (err) {
//         console.log('API key/usage error:', err);
//       }
//     }
//   };

//   /* ── Loading state ─────────────────────────────────────────────────── */
//   if (auth && !auth?.user && pageLoading) {
//     return (
//       <LoadingContainer>
//         <Typography variant="subtitle1" sx={{ color: colors.neutral[500] }}>
//           Please wait while we fetch user details...
//         </Typography>
//         <Box sx={{ height: 'auto', width: '25%', mx: 'auto', mt: 5 }}>
//           <LinearProgress />
//         </Box>
//       </LoadingContainer>
//     );
//   }

//   /* ── Dates ─────────────────────────────────────────────────────────── */
//   const endDate = new Date(subscriptionDetails?.current_period_end * 1000);
//   const formattedEndDate = endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
//   const daysUntilNextBillingCycle = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 3600 * 24));
//   const amount = subscriptionDetails?.plan?.amount / 100 || null;
//   const currency = subscriptionDetails?.plan?.currency?.toUpperCase() || null;
//   const formattedAmount = amount?.toLocaleString('en-US', { style: 'currency', currency: currency ?? 'usd' }) ?? null;

//   const userCreatedTime = creationTime
//     ? new Date(creationTime).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
//     : null;

//   let _apiKeyStartDate = null;
//   if (apiKeyStartDate) {
//     const d = new Date(apiKeyStartDate);
//     if (!isNaN(d.getTime())) {
//       _apiKeyStartDate = new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(d);
//     }
//   }

//   if (pageLoading || !auth?.user) return null;

//   return (
//     <Box sx={{ width: '100%', pb: 8 }}>

//       <SectionCard sx={{ mb: 4 }}>
//         <CardContent sx={{ p: { xs: 3, md: 4 } }}>
//           <Box sx={{ display: 'flex', alignItems: { xs: 'start', md: 'center' }, gap: 2.5, flexWrap: 'wrap' }}>
//             {/* Avatar */}
//             <Avatar
//               alt={displayName}
//               src={photoURL}
//               sx={{
//                 width: 64, height: 64,
//                 border: `3px solid ${colors.neutral[200]}`,
//                 boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
//               }}
//             />
//             {/* User info */}
//             <Box sx={{ flex: 1, minWidth: 200 }}>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
//                 <Typography sx={{
//                   fontWeight: 700, fontSize: '1.25rem', color: colors.neutral[900],
//                   letterSpacing: '-0.01em',
//                 }}>
//                   {displayName}
//                 </Typography>
//                 {isPremium && (
//                   <Chip
//                     icon={<VerifiedRoundedIcon sx={{ fontSize: '.85rem !important' }} />}
//                     label={userType}
//                     size="small"
//                     sx={{
//                       bgcolor: colors.accent[50], color: colors.accent.dark,
//                       fontWeight: 600, fontSize: '.72rem', textTransform: 'capitalize',
//                       border: `1px solid ${colors.accent[200]}`,
//                       '& .MuiChip-icon': { color: colors.accent.main },
//                     }}
//                   />
//                 )}
//               </Box>
//               <Typography sx={{ color: colors.neutral[500], fontSize: '.875rem', mt: 0.3 }}>
//                 {email}
//               </Typography>
//               <Typography sx={{ color: colors.neutral[400], fontSize: '.78rem', mt: 0.5, display: 'block' }}>
//                 Member since {userCreatedTime ?? creationTime}
//               </Typography>
//             </Box>
//             {/* Actions */}
//             <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
//               {!emailVerified && email && (
//                 <Button
//                   onClick={emailVerification}
//                   size="small"
//                   variant="outlined"
//                   sx={{
//                     textTransform: 'none', fontSize: '.8rem', fontWeight: 600,
//                     borderRadius: radii.sm, borderColor: colors.warning.main, color: colors.warning.dark,
//                     '&:hover': { bgcolor: colors.warning.bg, borderColor: colors.warning.dark },
//                   }}
//                 >
//                   Verify Email
//                 </Button>
//               )}
//               <Button
//                 onClick={logout}
//                 size="small"
//                 startIcon={<LogoutRoundedIcon sx={{ fontSize: '1rem !important' }} />}
//                 sx={{
//                   textTransform: 'none', fontSize: '.8rem', fontWeight: 500,
//                   borderRadius: radii.sm, color: colors.neutral[600],
//                   border: `1px solid ${colors.neutral[200]}`,
//                   '&:hover': { bgcolor: colors.neutral[50], borderColor: colors.neutral[300] },
//                 }}
//               >
//                 Sign out
//               </Button>
//             </Box>
//           </Box>
//         </CardContent>
//       </SectionCard>

//       {apiKey && (
//         <Collapse in={!!apiKey} timeout="auto" unmountOnExit>
//           <SectionCard sx={{ mb: 4 }}>
//             <CardContent sx={{ p: { xs: 3, md: 4 } }}>
//               <SectionHeader
//                 icon={<KeyRoundedIcon sx={{ fontSize: '1.1rem', color: colors.accent.main }} />}
//                 title="API Keys"
//                 subtitle="Restricted to your operations and usage limit"
//               />

//               <Box sx={{
//                 bgcolor: colors.neutral[50], borderRadius: '12px',
//                 border: `1px solid ${colors.neutral[200]}`,
//                 p: 2.5,
//               }}>
//                 {/* Key display row */}
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
//                   <Box sx={{
//                     flex: 1, minWidth: 200,
//                     fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
//                     fontSize: '.875rem', color: colors.neutral[800], fontWeight: 500,
//                     letterSpacing: '0.01em',
//                     cursor: 'pointer',
//                     '&:hover': { color: colors.accent.main },
//                     transition: 'color 150ms ease',
//                   }}
//                     onClick={() => handleClipboard(apiKey)}
//                   >
//                     {apiKey}
//                   </Box>
//                   <LightTooltip describeChild title={clipboard || 'Copy key'} placement="top" disableInteractive>
//                     <IconButton
//                       size="small"
//                       onClick={() => handleClipboard(apiKey)}
//                       sx={{
//                         bgcolor: colors.neutral[0], border: `1px solid ${colors.neutral[200]}`,
//                         borderRadius: radii.sm, '&:hover': { bgcolor: colors.accent[50], borderColor: colors.accent[200] },
//                       }}
//                     >
//                       <ContentCopyRoundedIcon sx={{ fontSize: '1rem', color: clipboard ? colors.success.main : colors.neutral[600] }} />
//                     </IconButton>
//                   </LightTooltip>
//                   {clipboard && (
//                     <Chip label={clipboard} size="small" sx={{
//                       bgcolor: colors.success.bg, color: colors.success.dark,
//                       fontWeight: 600, fontSize: '.72rem', border: `1px solid ${colors.success.light}30`,
//                     }} />
//                   )}
//                 </Box>

//                 {/* Meta info */}
//                 <Box sx={{ mt: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
//                   {_apiKeyStartDate && (
//                     <Typography sx={{ color: colors.neutral[500], fontSize: '.78rem' }}>
//                       Created {_apiKeyStartDate}
//                     </Typography>
//                   )}
//                   {email && (
//                     <Typography sx={{ color: colors.neutral[500], fontSize: '.78rem' }}>
//                       <Link target="_blank" rel="noopener noreferrer" underline="hover" href="/docs/api?lang=r#api-reference"
//                         sx={{ color: colors.accent.main, '&:hover': { color: colors.accent.dark } }}>
//                         API Documentation <OpenInNewRoundedIcon sx={{ fontSize: '.7rem', verticalAlign: 'middle' }} />
//                       </Link>
//                     </Typography>
//                   )}
//                 </Box>
//               </Box>

//               {/* Usage toggle */}
//               {apiKeyQuota?.length > 0 && (
//                 <Box sx={{ mt: 2 }}>
//                   <Button
//                     size="small"
//                     onClick={() => setOpen(!open)}
//                     endIcon={open ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
//                     sx={{
//                       textTransform: 'none', fontSize: '.84rem', fontWeight: 500,
//                       color: colors.neutral[600], '&:hover': { bgcolor: colors.neutral[50] },
//                     }}
//                   >
//                     {open ? 'Hide' : 'View'} Usage History
//                   </Button>
//                   <Collapse in={open} timeout="auto" unmountOnExit>
//                     <Box sx={{ mt: 1.5 }}>
//                       <QuotaTable data={apiKeyQuota} startDate={apiKeyStartDate} endDate={apiKeyEndDate} />
//                     </Box>
//                   </Collapse>
//                 </Box>
//               )}
//             </CardContent>
//           </SectionCard>
//         </Collapse>
//       )}

//       <SectionCard sx={{ mb: 4 }}>
//         <CardContent sx={{ p: { xs: 3, md: 4 } }}>
//           <SectionHeader
//             icon={<AppsRoundedIcon sx={{ fontSize: '1.1rem', color: colors.accent.main }} />}
//             title="Connected Apps"
//             subtitle="Manage third-party applications with access to your Grufity data"
//           />

//           {appsLoading ? (
//             <Box sx={{ py: 3, textAlign: 'center' }}>
//               <CircularProgress size={24} sx={{ color: colors.accent.main }} />
//             </Box>
//           ) : connectedApps && connectedApps.length > 0 ? (
//             <Box sx={{ border: `1px solid ${colors.neutral[200]}`, borderRadius: '12px', overflow: 'hidden' }}>
//               <List disablePadding>
//                 {connectedApps.map((app, idx) => (
//                   <Box key={app.clientId}>
//                     <ListItem sx={{ py: 2, px: 2.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, bgcolor: '#ffffff' }}>
//                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 200 }}>
//                         <Avatar sx={{ bgcolor: colors.neutral[50], color: colors.neutral[800], width: 44, height: 44, border: `1px solid ${colors.neutral[200]}` }}>
//                           {getAppIcon(app.clientName)}
//                         </Avatar>
//                         <Box>
//                           <Typography sx={{ fontWeight: 600, color: colors.neutral[900], fontSize: '.95rem' }}>
//                             {app.clientName || 'Unknown App'}
//                           </Typography>
//                           <Typography sx={{ fontSize: '.78rem', color: colors.neutral[500], mt: 0.2, display: 'block' }}>
//                             Connected on {new Date(app.approvedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
//                           </Typography>
//                         </Box>
//                       </Box>

//                       <Button
//                         size="small"
//                         variant="outlined"
//                         color="error"
//                         onClick={() => handleRevokeApp(app.clientId)}
//                         disabled={revoking === app.clientId}
//                         startIcon={revoking === app.clientId ? <CircularProgress size={14} color="inherit" /> : <LinkOffRoundedIcon sx={{ fontSize: '1.1rem !important' }} />}
//                         sx={{
//                           textTransform: 'none', borderRadius: radii.sm, fontWeight: 600,
//                           borderColor: colors.error.light, color: colors.error.main,
//                           '&:hover': { bgcolor: colors.error.bg, borderColor: colors.error.main },
//                           '&:disabled': { borderColor: colors.neutral[200], color: colors.neutral[400] }
//                         }}
//                       >
//                         {revoking === app.clientId ? 'Disconnecting...' : 'Disconnect'}
//                       </Button>
//                     </ListItem>
//                     {idx < connectedApps.length - 1 && <Divider />}
//                   </Box>
//                 ))}
//               </List>
//             </Box>
//           ) : (
//             <Box sx={{ textAlign: 'center', py: 5, px: 3, bgcolor: colors.neutral[50], borderRadius: 2, border: `1px solid ${colors.neutral[200]}` }}>
//               <Box sx={{ width: 48, height: 48, borderRadius: '50%', mx: 'auto', mb: 2, bgcolor: colors.neutral[200], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                 <AppsRoundedIcon sx={{ color: colors.neutral[500] }} />
//               </Box>
//               <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: colors.neutral[800], mb: 0.5 }}>
//                 No connected apps
//               </Typography>
//               <Typography sx={{ fontSize: '.875rem', color: colors.neutral[500], display: 'block' }}>
//                 You haven't granted access to any third-party AI agents yet.
//               </Typography>
//             </Box>
//           )}
//         </CardContent>
//       </SectionCard>

//       <SectionCard>
//         <CardContent sx={{ p: { xs: 3, md: 4 } }}>
//           <SectionHeader
//             icon={<ReceiptLongRoundedIcon sx={{ fontSize: '1.1rem', color: colors.accent.main }} />}
//             title="Subscription & Billing"
//             subtitle="Manage your plan and payment details"
//             action={isPremium && (
//               <Chip
//                 icon={<FiberManualRecordIcon sx={{ fontSize: '.6rem !important' }} />}
//                 label={subscriptionDetails?.status || 'active'}
//                 size="small"
//                 color={getStatusColor(subscriptionDetails?.status || 'active')}
//                 sx={{ textTransform: 'capitalize', fontSize: '.75rem', fontWeight: 600 }}
//               />
//             )}
//           />

//           {(formattedAmount || cardDetails?.last4) ? (
//             <Stack direction={{ md: 'row', xs: 'column' }} spacing={2.5}>
//               {/* Plan card */}
//               {formattedAmount && (
//                 <Grow in={!!formattedAmount} timeout={800}>
//                   <Box sx={{
//                     flex: 1, bgcolor: colors.neutral[50], borderRadius: '12px',
//                     border: `1px solid ${colors.neutral[200]}`, p: 3,
//                     transition: 'border-color 200ms ease',
//                     '&:hover': { borderColor: colors.neutral[300] },
//                   }}>
//                     <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
//                       <Box>
//                         <Typography sx={{ fontSize: '.78rem', fontWeight: 600, color: colors.neutral[500], textTransform: 'uppercase', letterSpacing: '0.06em' }}>
//                           {subscriptionDetails?.plan?.nickname ?? 'Current Plan'}
//                         </Typography>
//                       </Box>
//                     </Box>
//                     <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1.5 }}>
//                       <Typography sx={{ fontWeight: 700, fontSize: '2rem', color: colors.neutral[900], lineHeight: 1, letterSpacing: '-0.02em' }}>
//                         {formattedAmount}
//                       </Typography>
//                       {subscriptionDetails?.plan?.interval && (
//                         <Typography sx={{ ml: 0.5, fontSize: '.84rem', color: colors.neutral[500] }}>
//                           /{subscriptionDetails.plan.interval}
//                         </Typography>
//                       )}
//                     </Box>
//                     {formattedEndDate && (
//                       daysUntilNextBillingCycle <= 0 ? (
//                         <Alert severity="error" sx={{ py: 0.5, fontSize: '.78rem', borderRadius: radii.sm }}>
//                           Payment overdue — please update billing details.
//                         </Alert>
//                       ) : daysUntilNextBillingCycle <= 7 ? (
//                         <Alert severity="warning" sx={{ py: 0.5, fontSize: '.78rem', borderRadius: radii.sm }}>
//                           Renews in <strong>{daysUntilNextBillingCycle} days</strong> on {formattedEndDate}
//                         </Alert>
//                       ) : (
//                         <Typography sx={{ fontSize: '.82rem', color: colors.neutral[500] }}>
//                           Renews {formattedEndDate} &middot; {daysUntilNextBillingCycle} days
//                         </Typography>
//                       )
//                     )}
//                     <Button
//                       size="small" variant="outlined" endIcon={<CallMadeRoundedIcon sx={{ fontSize: '.85rem !important' }} />}
//                       onClick={handleManagePortal}
//                       sx={{
//                         mt: 2.5, textTransform: 'none', fontSize: '.84rem', fontWeight: 600,
//                         borderRadius: radii.sm, borderColor: colors.neutral[300], color: colors.neutral[700],
//                         '&:hover': { bgcolor: colors.neutral[100], borderColor: colors.neutral[400] },
//                       }}
//                     >
//                       Manage plan
//                     </Button>
//                   </Box>
//                 </Grow>
//               )}

//               {/* Payment method card */}
//               {cardDetails?.last4 && (
//                 <Grow in={!!cardDetails?.last4} timeout={1000}>
//                   <Box sx={{
//                     flex: 1, bgcolor: colors.neutral[50], borderRadius: '12px',
//                     border: `1px solid ${colors.neutral[200]}`, p: 3,
//                     transition: 'border-color 200ms ease',
//                     '&:hover': { borderColor: colors.neutral[300] },
//                   }}>
//                     <Typography sx={{ fontSize: '.78rem', fontWeight: 600, color: colors.neutral[500], textTransform: 'uppercase', letterSpacing: '0.06em', mb: 2 }}>
//                       Payment Method
//                     </Typography>
//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
//                       <Box sx={{
//                         width: 40, height: 28, bgcolor: colors.neutral[200], borderRadius: 4,
//                         display: 'flex', alignItems: 'center', justifyContent: 'center',
//                       }}>
//                         <CreditCardRoundedIcon sx={{ fontSize: '1.1rem', color: colors.neutral[500] }} />
//                       </Box>
//                       <Box>
//                         <Typography sx={{ fontWeight: 600, fontSize: '.9rem', color: colors.neutral[800] }}>
//                           {cardDetails.brand?.toUpperCase()} •••• {cardDetails.last4}
//                         </Typography>
//                         <Typography sx={{ fontSize: '.78rem', color: colors.neutral[500] }}>
//                           Expires {cardDetails.exp_month}/{cardDetails.exp_year}
//                         </Typography>
//                       </Box>
//                       {cardDetails.default && (
//                         <Chip label="Default" size="small" sx={{
//                           ml: 'auto', bgcolor: colors.success.bg, color: colors.success.dark,
//                           fontSize: '.7rem', fontWeight: 600, border: `1px solid ${colors.success.light}30`,
//                         }} />
//                       )}
//                     </Box>
//                     <Typography sx={{ fontSize: '.78rem', color: colors.neutral[500], mt: 0.5 }}>
//                       Associated with {cardDetails.email}
//                     </Typography>
//                     <Button
//                       size="small" variant="outlined" endIcon={<CallMadeRoundedIcon sx={{ fontSize: '.85rem !important' }} />}
//                       onClick={handleManagePortal}
//                       sx={{
//                         mt: 2.5, textTransform: 'none', fontSize: '.84rem', fontWeight: 600,
//                         borderRadius: radii.sm, borderColor: colors.neutral[300], color: colors.neutral[700],
//                         '&:hover': { bgcolor: colors.neutral[100], borderColor: colors.neutral[400] },
//                       }}
//                     >
//                       Update payment
//                     </Button>
//                   </Box>
//                 </Grow>
//               )}
//             </Stack>
//           ) : (
//             /* ── No subscription / loading state ─────────────────────── */
//             <Box sx={{
//               textAlign: 'center', py: 5, px: 3,
//               bgcolor: colors.neutral[50], borderRadius: 2,
//               border: `1px solid ${colors.neutral[200]}`,
//             }}>
//               {isPremium && !stripe_customer_id ? (
//                 <>
//                   <Typography sx={{ color: colors.neutral[500], fontSize: '.9rem', mb: 2, display: 'block' }}>
//                     We&apos;re having difficulty loading your subscription details.
//                   </Typography>
//                   <Button
//                     size="small" variant="outlined" endIcon={<CallMadeRoundedIcon />}
//                     onClick={() => router.push(STRIPE_PORTAL_URL)}
//                     sx={{ textTransform: 'none', borderRadius: radii.sm, fontWeight: 600 }}
//                   >
//                     View on Stripe Portal
//                   </Button>
//                 </>
//               ) : isPremium ? (
//                 <CircularProgress size={28} sx={{ color: colors.accent.main }} />
//               ) : (
//                 <>
//                   <Box sx={{
//                     width: 48, height: 48, borderRadius: '50%', mx: 'auto', mb: 2,
//                     bgcolor: colors.accent[50], display: 'flex', alignItems: 'center', justifyContent: 'center',
//                   }}>
//                     <ReceiptLongRoundedIcon sx={{ color: colors.accent.main }} />
//                   </Box>
//                   <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: colors.neutral[800], mb: 0.5 }}>
//                     No active subscription
//                   </Typography>
//                   <Typography sx={{ fontSize: '.875rem', color: colors.neutral[500], mb: 3, maxWidth: 320, mx: 'auto', display: 'block' }}>
//                     Upgrade to Premium for unlimited access to all Grufity features and data.
//                   </Typography>
//                   <Button
//                     variant="contained"
//                     href="/pricing?ref=profile"
//                     disableElevation
//                     sx={{
//                       textTransform: 'none', px: 4, py: 1.2, fontWeight: 600,
//                       fontSize: '.9rem', borderRadius: radii.sm,
//                       bgcolor: colors.accent.main, '&:hover': { bgcolor: colors.accent.dark },
//                     }}
//                   >
//                     {hasPastSubscriptions ? 'Reactivate Subscription' : 'Upgrade to Premium'}
//                   </Button>
//                 </>
//               )}
//             </Box>
//           )}
//         </CardContent>
//       </SectionCard>

//       <Box sx={{
//         mt: 6, pt: 4, borderTop: `1px solid ${colors.neutral[200]}`,
//         display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//         flexWrap: 'wrap', gap: 2,
//       }}>
//         <Typography sx={{ color: colors.neutral[400], fontSize: '.82rem' }}>
//           Need to delete your account? Contact us at{' '}
//           <Link href="mailto:contact@grufity.com?subject=Regarding My Account"
//             sx={{ color: colors.accent.main, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
//             contact@grufity.com
//           </Link>
//         </Typography>
//       </Box>

//       <Dialog
//         open={loading} maxWidth="xs"
//         sx={{
//           '& .MuiPaper-root.MuiDialog-paper': { backgroundColor: 'transparent', boxShadow: 0 },
//           '& .MuiDialog-container': { backdropFilter: 'blur(8px) !important' },
//         }}
//       >
//         <DialogContent>
//           <DialogContentText sx={{ textAlign: 'center' }}>
//             <Box sx={{ mb: 3 }}>
//               <CircularProgress sx={{ color: '#fff' }} />
//             </Box>
//             <Typography sx={{ color: '#fff', fontSize: '.9rem', fontWeight: 500 }}>
//               Redirecting to Stripe...
//             </Typography>
//           </DialogContentText>
//         </DialogContent>
//       </Dialog>

//       {/* Email verification dialog */}
//       <Dialog
//         open={alertOpen}
//         onClose={() => location.reload()}
//         PaperProps={{ sx: { borderRadius: radii.lg, maxWidth: 420 } }}
//       >
//         <DialogContent sx={{ textAlign: 'center', px: 4, py: 4 }}>
//           <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', color: colors.neutral[900], mb: 2 }}>
//             Verification email sent
//           </Typography>
//           <Typography sx={{ color: colors.neutral[600], fontSize: '.9rem', lineHeight: 1.6 }}>
//             We sent a verification link to{' '}
//             <Typography component="span" sx={{ color: colors.accent.main, fontWeight: 600 }}>{email}</Typography>.
//             If you don&apos;t see it, check your spam folder.
//           </Typography>
//           <Button
//             onClick={() => location.reload()}
//             variant="contained"
//             disableElevation
//             sx={{
//               mt: 3, textTransform: 'none', fontWeight: 600,
//               borderRadius: radii.sm, bgcolor: colors.accent.main,
//               '&:hover': { bgcolor: colors.accent.dark },
//             }}
//             autoFocus
//           >
//             Done
//           </Button>
//         </DialogContent>
//       </Dialog>
//     </Box>
//   );
// };

// export default ProfileTabs;
